import { useEffect, useState, useRef, useMemo } from "react"
import { router } from "@inertiajs/react"
import cable from "@/lib/cable"
import { Button } from "@/components/ui/button"

type ImportStatus = "pending" | "scraping" | "completed" | "failed"

type ImportMessage = {
  event: "import_progress" | "listing_updated"
  status?: ImportStatus
  detail?: string
  address?: string
  price?: string
}

const IMPORT_STEPS = [
  { key: "loading", match: /loading listing/i, label: "Loading listing page", hint: "Visiting the listing page and reading through everything — just like a buyer would." },
  { key: "extracting", match: /extracting property/i, label: "Extracting details", hint: "Pulling out the address, price, bedrooms, and all the key details." },
  { key: "updating", match: /updating listing/i, label: "Saving details", hint: "Your listing is taking shape — details locked in." },
  { key: "downloading", match: /download/i, label: "Downloading photos", hint: "Grabbing every photo and filtering out floor plans and EPC charts." },
  { key: "analysing", match: /analysing photos/i, label: "Analysing photos", hint: "Reviewing each photo so your videos know exactly what they're working with." },
] as const

function resolveStep(detail: string): number {
  for (let i = IMPORT_STEPS.length - 1; i >= 0; i--) {
    if (IMPORT_STEPS[i].match.test(detail)) return i
  }
  return 0
}

export default function ImportProgress({
  listingSlug,
  scrapeStatus,
}: {
  listingSlug: string
  scrapeStatus: ImportStatus
}) {
  const [status, setStatus] = useState<ImportStatus>(scrapeStatus)
  const [detail, setDetail] = useState<string>(
    scrapeStatus === "pending" ? "Queued for import..." : "Importing..."
  )
  const [listingPreview, setListingPreview] = useState<{ address: string; price: string } | null>(null)
  const subscriptionRef = useRef<ReturnType<typeof cable.subscriptions.create> | null>(null)

  const currentStep = useMemo(() => resolveStep(detail), [detail])

  // Extract "Downloaded X of Y" or "Downloading X photos" from download messages
  const photoProgress = useMemo(() => {
    const downloaded = detail.match(/downloaded?\s+(\d+)\s+of\s+(\d+)/i)
    if (downloaded) return { current: parseInt(downloaded[1]), total: parseInt(downloaded[2]) }
    const downloading = detail.match(/downloading\s+(\d+)\s+photos/i)
    if (downloading) return { current: 0, total: parseInt(downloading[1]) }
    return null
  }, [detail])

  // Calculate overall progress percentage (0-100)
  const progress = useMemo(() => {
    if (status === "pending") return 0
    if (status === "completed") return 100
    if (status === "failed") return 0

    const stepSize = 98 / IMPORT_STEPS.length
    const baseProgress = currentStep * stepSize
    const downloadStepIndex = IMPORT_STEPS.findIndex(s => s.key === "downloading")

    // For the download step, interpolate within the segment
    if (currentStep === downloadStepIndex && photoProgress) {
      const photoPercent = (photoProgress.current / photoProgress.total) * stepSize
      return Math.min(baseProgress + photoPercent, 98)
    }

    // Add a small offset to show we're working on this step
    return Math.min(baseProgress + stepSize / 2, 98)
  }, [status, currentStep, photoProgress])

  useEffect(() => {
    if (scrapeStatus === "completed") return

    let unsubscribed = false

    const subscription = cable.subscriptions.create(
      { channel: "ListingChannel", slug: listingSlug },
      {
        received(data: ImportMessage) {
          if (data.event === "listing_updated") {
            if (data.address && data.price) {
              setListingPreview({ address: data.address, price: data.price })
            }
            return
          }

          if (data.status) setStatus(data.status)
          if (data.detail) setDetail(data.detail)

          if (data.status === "completed") {
            if (!unsubscribed) {
              unsubscribed = true
              subscription.unsubscribe()
            }
            clearInterval(poll)
            setTimeout(() => router.reload(), 500)
          }
        },
      }
    )

    subscriptionRef.current = subscription

    const poll = setInterval(() => {
      router.reload({ only: ["listing"] })
    }, 10000)

    return () => {
      if (!unsubscribed) {
        unsubscribed = true
        subscription.unsubscribe()
      }
      clearInterval(poll)
    }
  }, [listingSlug, scrapeStatus])

  if (status === "completed") return null

  const isFailed = status === "failed"

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone/50 bg-white">
      {/* Decorative background pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]">
        <svg className="size-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="import-grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0v32" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#import-grid)" />
        </svg>
      </div>

      <div className="relative px-8 py-10 sm:px-12 sm:py-14">
        {/* Icon and heading */}
        <div className="flex flex-col items-center text-center">
          {isFailed ? (
            <div className="mb-5 flex size-14 items-center justify-center rounded-2xl bg-red/8 ring-1 ring-red/10">
              <svg className="size-7 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </div>
          ) : (
            <div className="mb-5 flex size-14 items-center justify-center rounded-2xl bg-red/8 ring-1 ring-red/10">
              <svg className="size-7 text-red import-icon-pulse" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
            </div>
          )}

          <h2 className="font-display text-xl font-semibold tracking-tight text-charcoal">
            {isFailed ? "Import failed" : "Importing your listing"}
          </h2>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-warm-grey">
            {isFailed
              ? detail
              : "We're pulling in all the details, photos, and features. This usually takes under a minute."
            }
          </p>

          {/* Show listing details as soon as they're extracted */}
          {!isFailed && listingPreview && (
            <div className="mt-4 rounded-lg bg-cream/60 px-4 py-2.5 text-sm">
              <p className="font-medium text-charcoal">{listingPreview.address}</p>
              <p className="text-warm-grey">{listingPreview.price}</p>
            </div>
          )}
        </div>

        {/* Progress bar — only show when actively importing */}
        {!isFailed && (
          <div className="mx-auto mt-8 max-w-md">
            {/* Track */}
            <div className="relative h-1.5 overflow-hidden rounded-full bg-stone/60">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-red transition-all duration-700 ease-out"
                style={{ width: `${progress}%` }}
              />
              {/* Shimmer overlay on the filled portion */}
              <div
                className="absolute inset-y-0 left-0 overflow-hidden rounded-full"
                style={{ width: `${progress}%` }}
              >
                <div className="import-shimmer absolute inset-0" />
              </div>
            </div>

            {/* Step label + hint */}
            <p className="mt-4 text-center text-[13px] font-medium text-charcoal">
              {photoProgress
                ? `${photoProgress.current} of ${photoProgress.total} photos`
                : IMPORT_STEPS[currentStep]?.label ?? detail
              }
            </p>
            <p className="mt-1 text-center text-xs text-warm-grey">
              {IMPORT_STEPS[currentStep]?.hint}
            </p>
          </div>
        )}

        {/* Steps indicator — only show when actively importing */}
        {!isFailed && (
          <div className="mx-auto mt-8 flex max-w-xs items-center justify-center gap-1.5">
            {IMPORT_STEPS.map((step, i) => {
              const isComplete = i < currentStep
              const isCurrent = i === currentStep
              return (
                <div key={step.key} className="flex items-center gap-1.5">
                  <div
                    className={`flex items-center justify-center rounded-full transition-all duration-500 ${
                      isComplete
                        ? "size-4 bg-red"
                        : isCurrent
                          ? "size-4 bg-charcoal/10 ring-1 ring-charcoal/20"
                          : "size-2 bg-charcoal/15"
                    }`}
                    title={step.label}
                  >
                    {isComplete && (
                      <svg className="size-2.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    )}
                    {isCurrent && (
                      <div className="size-1.5 rounded-full bg-charcoal/40 import-dot-pulse" />
                    )}
                  </div>
                  {i < IMPORT_STEPS.length - 1 && (
                    <div
                      className={`h-px w-4 transition-colors duration-500 ${
                        isComplete ? "bg-red/40" : "bg-charcoal/10"
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Retry button for failed state */}
        {isFailed && (
          <div className="mt-6 flex justify-center">
            <Button
              className="bg-red text-white hover:bg-deep-red"
              onClick={() => router.post(`/dashboard/listings/${listingSlug}/scrape`)}
            >
              Try again
            </Button>
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        .import-icon-pulse {
          animation: import-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes import-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .import-dot-pulse {
          animation: import-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .import-shimmer {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.4) 50%,
            transparent 100%
          );
          animation: import-shimmer-slide 1.8s ease-in-out infinite;
        }
        @keyframes import-shimmer-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  )
}
